import React from 'react';
import PropTypes from 'prop-types';
import { gettext } from '@eb/i18n';
import classNames from 'classnames';

import { TYPE_ERROR } from '@eb/eds-notification';
import { withMainControls } from '@eb/eds-structure';
import { Link } from '@eb/eds-link';
import { Modal } from '@eb/eds-modal';

import { AlertChunky } from '@eb/eds-iconography';

import PageHeader from '../../components/PageHeader';
import ModalMessage from '../../components/ModalMessage';

import SplitContainer from '../../connectedComponents/SplitContainer';
import ConnectedEventbriteLoginOptions from '../../connectedComponents/EventbriteLoginOptions';
import ConnectedSocialLoginOptions from '../../connectedComponents/ConnectedSocialLoginOptions';
import ToSModal from '../../connectedComponents/ToSModal';

import {
    TOKEN_USED_OR_EXPIRED_MESSAGE,
    TEXT_TOKEN_USED_ONCE,
    TOS_SOCIAL_MUST_BE_ACCEPTED,
} from '../../constants/messages';
import { INVALID_RESET_TOKEN, COMPLETE_SIGN_UP_PATH } from '../../constants';
import { TOSMODAL_SOCIAL_DENIED } from '../../constants/analytics';

import './LoginPage.scss';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/LOG_IN_PATH
 */
class LoginPage extends React.Component {
    static propTypes = {
        clearAuthNotification: PropTypes.func,
        handleSignupClick: PropTypes.func,
        launchAsyncStrategyForAuthSocialScripts: PropTypes.bool,
    };
    state = {
        showExpiredModal: false,
    };
    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;
        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });
            this._hasNotification = true;
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;
        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    componentDidMount() {
        const {
            setSignupSecondStep,
            cleanSocialAuthenticationData,
            clearAuthNotification,
            loadGoogleOneTap,
            launchGoogleOneTapWeb,
            setPasswordlessCreateAccountFlow,
            router: {
                location: { query },
            },
        } = this.props;
        if (setSignupSecondStep) {
            setSignupSecondStep(false);
        }
        if (launchGoogleOneTapWeb) {
            //Load google one tap
            loadGoogleOneTap?.();
        }

        //Clear notification and social data when page is loaded.
        clearAuthNotification();
        cleanSocialAuthenticationData();
        setPasswordlessCreateAccountFlow(false);

        /*This is needed here because the route is redirected on the server, before we have the
        chance to set state. SO once the component is loaded we can determine if this page was
        redirected to due to an invalid reset password token*/
        if (query[INVALID_RESET_TOKEN]) {
            this.setState({ showExpiredModal: true });
        }
    }

    _onSignupClick = (event) => {
        const { handleSignUpClick } = this.props;

        event?.preventDefault();

        handleSignUpClick?.(null, true);
    };
    _handleToSDenied = () => {
        const { cancelToS } = this.props;

        cancelToS({
            actionName: TOSMODAL_SOCIAL_DENIED,
            notificationMessage: TOS_SOCIAL_MUST_BE_ACCEPTED,
        });
    };
    _handleOnCloseExpiredModal = () => {
        this.setState({ showExpiredModal: false });
    };

    /**
     * Social login functions
     */

    render() {
        const {
            acceptSocialToS,
            launchAsyncStrategyForAuthSocialScripts,
            handleTrackBrandLogoClick,
        } = this.props;
        const pageHeaderProps = {
            headerText: gettext('Log in'),
            usesNewStyles: true,
            handleTrackBrandLogoClick,
        };
        const buttonCTA = gettext('Log in');
        const optionalSocialProps = launchAsyncStrategyForAuthSocialScripts
            ? { asyncStrategy: 'async' }
            : {};
        const { showExpiredModal } = this.state;
        const componentClasses = classNames(
            'eds-l-mar-vert-14',
            'login-status-content',
        );
        const modalLogin = (
            <Modal
                noMinHeight={true}
                isShown={showExpiredModal}
                onClose={this._handleOnCloseExpiredModal}
            >
                <div className="eds-l-pad-all-8 eds-align--center">
                    <div className={componentClasses}>
                        <ModalMessage
                            iconType={<AlertChunky />}
                            headerText={TOKEN_USED_OR_EXPIRED_MESSAGE}
                            expirationTimeText={TEXT_TOKEN_USED_ONCE}
                        />
                    </div>
                </div>
            </Modal>
        );

        return (
            <SplitContainer
                shouldShowSecondaryPanel={true}
                usesNewStyles={true}
                header={<PageHeader {...pageHeaderProps} />}
            >
                {modalLogin}
                <>
                    <ConnectedEventbriteLoginOptions
                        buttonCTA={buttonCTA}
                        handleShowNotification={this._handleShowNotification}
                        handleClearNotification={this._handleClearNotification}
                    />
                    <div className="split-container__divider eds-l-pad-bot-6">
                        <div className="split-container__divider-text eds-text-bm eds-text-weight--heavy">
                            {gettext('or')}
                        </div>
                    </div>

                    <div
                        className="eds-l-pad-bot-5"
                        data-testid="social-login-section"
                    >
                        <ConnectedSocialLoginOptions
                            buttonAppleText={gettext('Sign in with Apple')}
                            buttonFBText={gettext('Sign in with Facebook')}
                            buttonGoogleText={gettext('Sign in with Google')}
                            {...optionalSocialProps}
                        />
                    </div>

                    <div className="eds-text-color--ui-800 eds-text-weight--heavy eds-l-pad-bot-5 eds-l-pad-left-5">
                        <Link
                            onClick={this._onSignupClick}
                            type="button"
                            data-testid="go-to-signup"
                            to={COMPLETE_SIGN_UP_PATH}
                        >
                            {gettext('Sign up for Eventbrite')}
                        </Link>
                    </div>

                    <ToSModal
                        dataTestId="signup-tos"
                        onAcceptToS={acceptSocialToS}
                        onCancelToS={this._handleToSDenied}
                    />
                </>
            </SplitContainer>
        );
    }
}

export default withMainControls(LoginPage);
